import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

const AdminWorkLogPage = () => {
  const { users = [], sites = [] } = useOutletContext();
  
  // Date filter state - Single week selection (matches Payments page)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber(new Date()));
  
  // Calendar state (single week selection)
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Filter state
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  
  // State for data
  const [workLogData, setWorkLogData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalHours: 0,
    totalWorkers: 0,
    totalSites: 0
  });

  // Photo viewer state
  const [showPhotoPopup, setShowPhotoPopup] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Flag tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Work Diary Flagging System - Detection Logic
  const hasLocationMismatch = (entry) => {
    if (!entry.clockInLocation || !entry.clockOutLocation) {
      return false;
    }
    
    if (entry.clockOutMode === 'active') {
      return false;
    }
    
    return entry.clockInLocation !== entry.clockOutLocation;
  };

  const hasOfflineClockIn = (entry) => {
    return !entry.clockInLocation || entry.clockInLocation.trim() === '';
  };

  const hasOfflineClockOut = (entry) => {
    if (entry.clockOutMode === 'active') {
      return false;
    }
    
    return !entry.clockOutLocation || entry.clockOutLocation.trim() === '';
  };

  const hasForcedClockOut = (entry) => {
    return entry.clockOutMode === 'forced' || entry.clockOutMode === 'admin_override';
  };

  const getEntryFlags = (entry) => {
    const flags = [];
    
    if (hasLocationMismatch(entry)) {
      flags.push({
        type: 'location_mismatch',
        title: 'Location Mismatch',
        column: 'clock_out_location',
        message: 'The clock-in and clock-out locations are different.\n\nThis could indicate the worker moved sites during the day, or it could be due to GPS inaccuracy on their device.\n\nThis does not necessarily indicate any foul play.'
      });
    }
    
    if (hasOfflineClockIn(entry)) {
      flags.push({
        type: 'offline_clock_in',
        title: 'Offline Clock-In',
        column: 'clock_in_location',
        message: 'No GPS location recorded for clock-in.\n\nWorker may have clocked in offline or GPS was unavailable.\n\nThis could be due to:\n• Poor network connectivity\n• GPS disabled on device\n• Location services unavailable\n\nThis is a normal occurrence and does not indicate wrongdoing.'
      });
    }
    
    if (hasOfflineClockOut(entry)) {
      flags.push({
        type: 'offline_clock_out',
        title: 'Offline Clock-Out',
        column: 'clock_out_location',
        message: 'No GPS location recorded for clock-out.\n\nWorker may have clocked out offline or GPS was unavailable.\n\nThis could be due to:\n• Poor network connectivity\n• GPS disabled on device\n• Location services unavailable\n\nThis is a normal occurrence and does not indicate wrongdoing.'
      });
    }
    
    if (hasForcedClockOut(entry)) {
      flags.push({
        type: 'forced_clock_out',
        title: 'Forced Clock-Out',
        column: 'clock_out_mode',
        message: 'This session was automatically clocked out by the system.\n\nPossible reasons:\n• Worker forgot to clock out manually\n• System auto-logout at end of day\n• Admin intervention\n\nPlease verify the actual work hours with the worker if needed.'
      });
    }
    
    return flags;
  };

  const getCombinedTooltipContent = (entry) => {
    const flags = getEntryFlags(entry);
    
    if (flags.length === 0) {
      return null;
    }
    
    if (flags.length === 1) {
      return {
        title: flags[0].title,
        content: flags[0].message
      };
    }
    
    // Multiple flags
    const title = `Session Alerts (${flags.length})`;
    
    const flagMessages = flags.map(flag => 
      `• ${flag.title}\n  ${flag.message.split('\n\n')[0]}`
    ).join('\n\n');
    
    const content = 
      flagMessages + 
      '\n\n' +
      'These issues could be due to:\n' +
      '• Worker was offline or GPS was unavailable\n' +
      '• GPS inaccuracy on the device\n' +
      '• Worker moved between sites\n' +
      '• Worker forgot to clock out manually\n\n' +
      'This does not necessarily indicate foul play.\n\n' +
      'Please verify with the worker if you have concerns.';
    
    return {
      title,
      content
    };
  };

  const handleFlagMouseEnter = (event, flagData) => {
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + window.scrollX + rect.width / 2,
      y: rect.top + window.scrollY - 10
    });
    
    setActiveTooltip(flagData);
  };

  const handleFlagMouseLeave = () => {
    setActiveTooltip(null);
  };

  // Helper function to get week number (copied from AdminOvertimePage)
  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  // Helper function to get week dates (ISO week calculation - matches getWeekNumber)
  const getWeekDates = (year, weekNum) => {
    // Use UTC dates to match getWeekNumber logic exactly
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const jan1Weekday = jan1.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Convert Sunday (0) to 7 for ISO week calculation
    const jan1Day = jan1Weekday === 0 ? 7 : jan1Weekday;
    
    // Find first Thursday of the year (ISO week 1 contains Jan 4)
    const firstThursday = new Date(Date.UTC(year, 0, 1));
    firstThursday.setUTCDate(1 + (4 - jan1Day + 7) % 7);
    
    // Find the Monday of the first week
    const firstMonday = new Date(firstThursday);
    firstMonday.setUTCDate(firstThursday.getUTCDate() - 3);
    
    // Calculate the start of the requested week
    const weekStart = new Date(firstMonday);
    weekStart.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      dates.push({ date: date.toISOString().split('T')[0], day: date.getUTCDate() });
    }
    return dates;
  };

  // Get week start and end dates for display
  function getWeekStartEnd(year, weekNum) {
    const weekDates = getWeekDates(year, weekNum);
    const startDate = new Date(weekDates[0].date);
    const endDate = new Date(weekDates[6].date);
    
    return {
      start: startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
      end: endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
    };
  }

  // Format week display
  function formatWeekDisplay(year, weekNum) {
    const weekDates = getWeekStartEnd(year, weekNum);
    return `Week ${weekNum} (${weekDates.start} - ${weekDates.end})`;
  }

  // Handle date click in calendar (single week selection)
  function handleDateClick(date) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date);
    setSelectedYear(year);
    setSelectedWeek(weekNum);
    setShowCalendar(false);
  }

  // Generate calendar for a specific month
  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Calculate the number of days to go back to get to Monday
    const dayOffset = (firstDay.getDay() + 6) % 7;
    startDate.setDate(firstDay.getDate() - dayOffset);
    
    const weeks = [];
    let currentWeek = [];
    
    // Generate 6 weeks of dates
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  }

  // Go to current week
  const goToCurrentWeek = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    setSelectedYear(currentYearNum);
    setSelectedWeek(currentWeekNum);
  };

  // Check if currently viewing the current week
  const isCurrentWeekSelected = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    return selectedYear === currentYearNum && selectedWeek === currentWeekNum;
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() - 7);
    
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() + 7);
    
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  // Calendar Component - Single week selection (Dark Theme)
  const Calendar = ({ currentDate, onDateClick, onClose, selectedYear, selectedWeek }) => {
    const [displayDate, setDisplayDate] = useState(currentDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = useRef(null);
    
    // Close calendar when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const weekNum = getWeekNumber(date);
      return date.getFullYear() === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className="absolute z-[9999] mt-2 left-0">
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Select a Week</h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h4 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h4>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                <div key={index} className="text-center text-sm font-medium text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {weeks.map((week, weekIndex) => (
                week.map((date, dateIndex) => {
                  const isCurrentMonth = date.getMonth() === month;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = isSelectedWeek(date);
                  const isFutureDate = date > new Date();
                  
                  return (
                    <button
                      key={`${weekIndex}-${dateIndex}`}
                      onClick={() => !isFutureDate && onDateClick(date)}
                      disabled={isFutureDate}
                      className={`p-2 text-sm rounded-lg transition-colors ${
                        isFutureDate 
                          ? 'text-gray-600 cursor-not-allowed bg-slate-700/30' 
                          : isCurrentMonth 
                            ? 'text-white hover:bg-slate-700' 
                            : 'text-gray-500'
                      } ${
                        isSelected ? 'bg-purple-600 text-white' : ''
                      } ${
                        isToday && !isSelected ? 'ring-2 ring-purple-400' : ''
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  );
                })
              ))}
            </div>
            
            <div className="mt-4 text-sm text-gray-400">
              Click on any day to select that week
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to extract street and postcode from full address
  const getShortAddress = (fullAddress) => {
    if (!fullAddress) return 'N/A';
    
    // Split by comma
    const parts = fullAddress.split(',').map(p => p.trim());
    
    // Typically format is: "Street, City, Postcode, Country"
    // We want Street and Postcode
    if (parts.length >= 3) {
      // Return street (first part) and postcode (second to last part)
      return `${parts[0]}, ${parts[parts.length - 2]}`;
    } else if (parts.length === 2) {
      // Just street and postcode
      return fullAddress;
    } else {
      // Return as is
      return fullAddress;
    }
  };

  // Initialize with current week
  useEffect(() => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    
    setSelectedYear(currentYearNum);
    setSelectedWeek(currentWeekNum);
  }, []);

  // Select all sites and users by default when data is loaded
  useEffect(() => {
    if (sites.length > 0 && selectedSites.length === 0) {
      setSelectedSites(sites.map(site => site.id));
    }
  }, [sites]);

  useEffect(() => {
    if (users.length > 0 && selectedUsers.length === 0) {
      setSelectedUsers(users.map(user => user.id));
    }
  }, [users]);

  // Fetch work log data when filters change
  useEffect(() => {
    fetchWorkLogData();
  }, [selectedYear, selectedWeek, selectedSites, selectedUsers]);

  // Fetch work log data
  const fetchWorkLogData = async () => {
    setIsLoading(true);
    try {
      // Build query parameters - single week selection
      const params = {
        from_year: selectedYear,
        from_week: selectedWeek,
        to_year: selectedYear,
        to_week: selectedWeek
      };

      // Only add site/user filters if they're selected
      if (selectedSites.length > 0) {
        params.sites = selectedSites.join(',');
      }
      if (selectedUsers.length > 0) {
        params.users = selectedUsers.join(',');
      }

      console.log('Fetching work logs with params:', params);

      const response = await axios.get(`${API}/admin/work-logs`, {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Work logs response:', response.data);
      setWorkLogData(response.data.work_logs || []);
      setSummary(response.data.summary || { totalHours: 0, totalWorkers: 0, totalSites: 0 });
    } catch (error) {
      console.error('Error fetching work log data:', error);
      console.error('Error details:', error.response?.data);
      // Handle gracefully with empty state
      setWorkLogData([]);
      setSummary({ totalHours: 0, totalWorkers: 0, totalSites: 0 });
    }
    setIsLoading(false);
  };

  // Handle site toggle for pill selection
  const handleSiteSelect = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Handle user toggle for pill selection
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle select all sites
  const handleSelectAllSites = () => {
    if (selectedSites.length === sites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sites.map(s => s.id));
    }
  };

  // Handle select all users
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit'  // Use 2-digit year (25 instead of 2025)
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Photo click handler
  const handlePhotoClick = (photos) => {
    if (photos && photos.length > 0) {
      setSelectedPhotos(photos);
      setCurrentPhotoIndex(0);
      setShowPhotoPopup(true);
    }
  };

  // Navigate photos in popup
  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % selectedPhotos.length);
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + selectedPhotos.length) % selectedPhotos.length);
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Work Diary</h1>
        <p className="text-purple-200">Track attendance, hours, and productivity across all sites</p>
      </div>

      {/* Filters Section - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Week Selection</h2>
          </div>
          
          {/* Current Week Button - Only active when not on current week */}
          <button
            onClick={goToCurrentWeek}
            disabled={isCurrentWeekSelected()}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center ${
              isCurrentWeekSelected()
                ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        {/* Sites and Team Filters - Pill Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Site Selection - Pill Style */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-gray-300 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Sites ({selectedSites.length} selected)
              </h4>
              <button
                onClick={handleSelectAllSites}
                className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
              >
                {selectedSites.length === sites.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
              {sites.map(site => (
                <button
                  key={site.id}
                  onClick={() => handleSiteSelect(site.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    selectedSites.includes(site.id)
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-600'
                  }`}
                >
                  {site.site_name}
                </button>
              ))}
            </div>
          </div>

          {/* Team Selection - Pill Style */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-gray-300 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Team ({selectedUsers.length} selected)
              </h4>
              <button
                onClick={handleSelectAllUsers}
                className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
              >
                {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
              {users.map(user => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    selectedUsers.includes(user.id)
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-600'
                  }`}
                >
                  {user.full_name || user.username}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Week Selection and Flagged Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Week Selection with Navigation */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              Week Selection
            </h4>
            <div className="flex items-center space-x-2">
              {/* Previous Week Button */}
              <button
                onClick={goToPreviousWeek}
                className="p-2 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Week Display Button */}
              <button
                onClick={() => setShowCalendar(true)}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span>{formatWeekDisplay(selectedYear, selectedWeek)}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {/* Next Week Button */}
              <button
                onClick={goToNextWeek}
                disabled={isCurrentWeekSelected()}
                className={`p-2 border rounded-lg transition-colors ${
                  isCurrentWeekSelected()
                    ? 'bg-slate-800 border-slate-700 text-gray-600 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-600 hover:bg-slate-700 text-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {showCalendar && (
              <Calendar
                currentDate={calendarDate}
                onDateClick={handleDateClick}
                onClose={() => setShowCalendar(false)}
                selectedYear={selectedYear}
                selectedWeek={selectedWeek}
              />
            )}
          </div>

          {/* Flagged Sessions Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative group">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <span className="mr-1">🚩</span>
              Show Flagged Only
              {/* Info Icon with Tooltip */}
              <span className="ml-1 text-gray-400 hover:text-purple-400 cursor-help relative">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                
                {/* Tooltip */}
                <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-900 text-white text-xs rounded-lg shadow-xl p-3 z-50 pointer-events-none border border-slate-600">
                  <div className="font-semibold mb-1">What triggers a flag?</div>
                  <ul className="space-y-1 text-gray-300">
                    <li>• Clock-in and clock-out locations differ</li>
                    <li>• GPS data missing at clock-in</li>
                    <li>• GPS data missing at clock-out</li>
                    <li>• Session ended by system auto-logout</li>
                  </ul>
                  <div className="mt-2 text-gray-400 italic">
                    Flagged sessions may require manual review.
                  </div>
                  {/* Arrow */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                </div>
              </span>
            </h4>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showFlaggedOnly}
                onChange={(e) => setShowFlaggedOnly(e.target.checked)}
                className="form-checkbox h-4 w-4 text-amber-600 rounded border-slate-500 bg-slate-700 focus:ring-amber-500 focus:ring-offset-0 focus:ring-1"
              />
              <span className="ml-2 text-sm text-gray-300">
                Sessions with alerts
                {showFlaggedOnly && workLogData.filter(log => getEntryFlags(log).length > 0).length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs font-semibold border border-amber-500/30">
                    {workLogData.filter(log => getEntryFlags(log).length > 0).length}
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Summary Cards - Dark Theme */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500/20 text-blue-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Total Hours</p>
              <p className="text-2xl font-bold text-white">{formatDuration(summary.totalHours)}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500/20 text-green-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Active Workers</p>
              <p className="text-2xl font-bold text-white">{summary.totalWorkers}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-500/20 text-purple-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Active Sites</p>
              <p className="text-2xl font-bold text-white">{summary.totalSites}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Work Log Table - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Work Diary Details</h2>
        </div>
        
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
              <p className="mt-4 text-gray-400">Loading work logs...</p>
            </div>
          ) : workLogData.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-white">No work logs found</h3>
              <p className="mt-1 text-sm text-gray-400">Try adjusting your filters or date range</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6">
              <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-16">
                    Selfie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Clock In Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Clock Out Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Clock In
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Clock Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider w-20">
                    Mode
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    GPS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Photos
                  </th>
                </tr>
              </thead>
              <tbody>
                {workLogData
                  .filter(log => {
                    // Apply flagged filter if enabled
                    if (showFlaggedOnly) {
                      return getEntryFlags(log).length > 0;
                    }
                    return true;
                  })
                  .map((log, index) => (
                  <tr key={index} className={`hover:bg-slate-700/50 transition-colors ${index !== workLogData.length - 1 ? 'border-b border-slate-700/50' : ''}`}>
                    {/* Team Member Column */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {log.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white flex items-center gap-2">
                            {log.username}
                            {/* Master flag - shows if ANY issue */}
                            {getEntryFlags(log).length > 0 && (
                              <span
                                className="flag-icon cursor-pointer text-base hover:text-amber-400 transition-colors"
                                onMouseEnter={(e) => handleFlagMouseEnter(e, getCombinedTooltipContent(log))}
                                onMouseLeave={handleFlagMouseLeave}
                              >
                                🚩
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Selfie Column */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      {(log.selfieUrl || log.fallbackSelfieUrl) ? (
                        <button
                          onClick={() => {
                            const selfieToShow = log.fallbackSelfieUrl || log.selfieUrl;
                            setSelectedPhotos([{gcs_url: selfieToShow, filename: log.clockInFallbackMode ? 'Fallback Selfie' : 'Selfie', upload_timestamp: log.date}]);
                            setCurrentPhotoIndex(0);
                            setShowPhotoPopup(true);
                          }}
                          className={`flex-shrink-0 h-8 w-8 rounded-lg overflow-hidden border-2 hover:border-purple-400 transition-colors cursor-pointer ${
                            log.clockInFallbackMode ? 'border-orange-400' : 'border-slate-600'
                          }`}
                          title={log.clockInFallbackMode ? 'Fallback selfie (no GPS)' : 'Regular selfie'}
                        >
                          <img
                            src={log.fallbackSelfieUrl || log.selfieUrl}
                            alt={log.clockInFallbackMode ? 'Fallback Selfie' : 'Selfie'}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">-</span>
                      )}
                    </td>
                    
                    {/* Site Column */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{log.siteName || 'N/A'}</div>
                    </td>
                    
                    {/* Clock In Location */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm flex items-center gap-1 ${hasOfflineClockIn(log) ? 'text-amber-400' : 'text-gray-300'}`}>
                        {getShortAddress(log.clockInLocation)}
                        {hasOfflineClockIn(log) && (
                          <span className="text-amber-400 text-xs">⚠️</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Clock Out Location */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm flex items-center gap-1 ${
                        hasLocationMismatch(log) ? 'text-amber-400' : 
                        hasOfflineClockOut(log) ? 'text-amber-400' : 'text-gray-300'
                      }`}>
                        {log.clockOutMode === 'active' ? (
                          <span className="text-green-400 font-medium">Currently Active</span>
                        ) : (
                          <>
                            {getShortAddress(log.clockOutLocation)}
                            {(hasLocationMismatch(log) || hasOfflineClockOut(log)) && (
                              <span className="text-amber-400 text-xs">⚠️</span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    
                    {/* Clock In Time */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{formatShortDate(log.date)}</div>
                      <div className="text-xs text-gray-400">{log.clockInTime}</div>
                    </td>
                    
                    {/* Clock Out Time */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {log.clockOutMode === 'active' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          <span className="w-2 h-2 mr-1 rounded-full bg-green-400 animate-pulse"></span>
                          Active
                        </span>
                      ) : (
                        <>
                          <div className="text-sm text-gray-300">{formatShortDate(log.date)}</div>
                          <div className="text-xs text-gray-400">{log.clockOutTime}</div>
                        </>
                      )}
                    </td>
                    
                    {/* Duration */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                        {log.clockOutMode === 'active' ? 'In Progress' : formatDuration(log.duration)}
                      </span>
                    </td>
                    
                    {/* Mode Column */}
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      {log.clockOutMode === 'active' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          Active
                        </span>
                      ) : hasForcedClockOut(log) ? (
                        <span 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 cursor-pointer border border-amber-500/30"
                          onMouseEnter={(e) => handleFlagMouseEnter(e, {
                            title: 'Forced Clock-Out',
                            content: 'This session was automatically clocked out by the system.\n\nPossible reasons:\n• Worker forgot to clock out manually\n• System auto-logout at end of day\n• Admin intervention'
                          })}
                          onMouseLeave={handleFlagMouseLeave}
                        >
                          Forced ⚠️
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          Manual
                        </span>
                      )}
                    </td>
                    
                    {/* GPS Column */}
                    <td className="px-2 py-4 whitespace-nowrap text-center">
                      {(hasOfflineClockIn(log) || hasOfflineClockOut(log)) ? (
                        <span className="text-amber-400 text-lg" title="Missing GPS data">⚠️</span>
                      ) : (
                        <span className="text-green-400 text-lg" title="GPS data available">✓</span>
                      )}
                    </td>
                    
                    {/* Photos Column */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.photos && log.photos.length > 0 ? (
                        <button
                          onClick={() => handlePhotoClick(log.photos)}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors border border-blue-500/30"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {log.photos.length} photo{log.photos.length > 1 ? 's' : ''}
                        </button>
                      ) : (
                        <span className="text-gray-500 text-xs">No photos</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
      
      {/* Photo Viewer Popup */}
      {showPhotoPopup && selectedPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 text-white">
              <h3 className="text-xl font-semibold">
                Photo {currentPhotoIndex + 1} of {selectedPhotos.length}
              </h3>
              <button
                onClick={() => setShowPhotoPopup(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Image Container */}
            <div className="flex-1 flex items-center justify-center mb-4 bg-black/50 rounded-lg p-4">
              <img
                src={selectedPhotos[currentPhotoIndex].gcs_url}
                alt={`Work diary photo ${currentPhotoIndex + 1}`}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>

            {/* Photo Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4 text-white">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-300">Filename:</span>
                  <span className="ml-2 font-medium">{selectedPhotos[currentPhotoIndex].filename}</span>
                </div>
                <div>
                  <span className="text-gray-300">Upload Time:</span>
                  <span className="ml-2 font-medium">
                    {new Date(selectedPhotos[currentPhotoIndex].upload_timestamp).toLocaleString('en-GB')}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            {selectedPhotos.length > 1 && (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handlePrevPhoto}
                  className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg backdrop-blur-sm transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <span className="text-white font-medium">
                  {currentPhotoIndex + 1} / {selectedPhotos.length}
                </span>
                <button
                  onClick={handleNextPhoto}
                  className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg backdrop-blur-sm transition-all flex items-center gap-2"
                >
                  Next
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Flag Tooltip */}
      {activeTooltip && (
        <div 
          className="fixed z-50 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-4 max-w-sm pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🚩</span>
            <h4 className="text-sm font-semibold text-white">
              {activeTooltip.title}
            </h4>
          </div>
          
          {/* Content */}
          <div className="text-xs text-gray-300 leading-relaxed whitespace-pre-line">
            {activeTooltip.content}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWorkLogPage;